import { useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminQueries, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { LOCALE_LABELS, USER_STATUS, userCreateSchema, userUpdateSchema } from '@blimo/shared';
import {
  applyProblemToForm,
  Button,
  CheckboxField,
  Form,
  FormRootError,
  InputField,
  SelectField,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@blimo/ui';
import { RoleCheckboxes } from './role-checkboxes.jsx';
import { PartyPicker } from '../meters/party-picker.jsx';
import { assignableRoles, toDateInput } from './user-utils.js';

const LOCALE_OPTIONS = Object.entries(LOCALE_LABELS).map(([value, label]) => ({ value, label }));
const STATUS_OPTIONS = Object.entries(USER_STATUS).map(([value, meta]) => ({ value, label: meta.label }));

/**
 * Create (user = null) or edit a user in a side sheet.
 * @param {{ user: any | null, open: boolean, onOpenChange: (open: boolean) => void, onCreated?: (user: any) => void, party?: { id: number, display_name?: string, email?: string|null } | null }} props
 *   `party` pre-selects the linked person when creating (e.g. from the person detail page).
 */
export function UserFormSheet({ user, open, onOpenChange, onCreated, party = null }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{user ? 'Benutzer bearbeiten' : 'Benutzer anlegen'}</SheetTitle>
          <SheetDescription>{user ? user.email : 'Das Konto wird im Status „Eingeladen“ angelegt.'}</SheetDescription>
        </SheetHeader>
        {open && <UserForm user={user} party={party} onDone={() => onOpenChange(false)} onCreated={onCreated} />}
      </SheetContent>
    </Sheet>
  );
}

function UserForm({ user, party, onDone, onCreated }) {
  const api = useApi();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const create = !user;
  const { data: allRoles } = useQuery(adminQueries.roles(api));
  const roles = assignableRoles(allRoles, me);
  const hiddenCount = (allRoles?.length ?? 0) - roles.length;

  const form = useForm({
    resolver: zodResolver(create ? userCreateSchema : userUpdateSchema),
    defaultValues: create
      ? {
          name: party?.display_name ?? '',
          email: party?.email ?? '',
          roles: party ? ['owner'] : [],
          locale: 'de',
          access_expires_at: '',
          send_invitation: true,
          party_id: party ? String(party.id) : '',
        }
      : {
          name: user.name ?? '',
          email: user.email ?? '',
          roles: user.roles ?? [],
          locale: user.locale ?? 'de',
          access_expires_at: toDateInput(user.access_expires_at),
          status: user.status ?? 'active',
          party_id: user.party_id ? String(user.party_id) : '',
        },
  });

  const mutation = useMutation({
    mutationFn: (payload) => (create ? api.admin.users.create(payload) : api.admin.users.update(user.id, payload)),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['blimo', 'admin', 'users', 'list'] });
      queryClient.setQueryData(keys.admin.detail('users', saved.id), saved);
      queryClient.invalidateQueries({ queryKey: keys.admin.dashboard() });
      // Personen-Detail zeigt das verknüpfte Konto
      queryClient.invalidateQueries({ queryKey: ['blimo', 'admin', 'parties'] });
      toast.success(create ? 'Benutzer angelegt' : 'Benutzer gespeichert');
      onDone();
      if (create) onCreated?.(saved);
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    const expires = values.access_expires_at || null;
    let payload;
    const partyId = values.party_id ? Number(values.party_id) : null;
    if (create) {
      payload = { ...values, name: values.name.trim(), access_expires_at: expires, party_id: partyId };
    } else {
      // Only send what changed – an unchanged role list must not be re-assigned (audit log, policy check).
      const dirty = form.formState.dirtyFields;
      payload = {};
      for (const key of ['name', 'email', 'status', 'locale']) if (dirty[key]) payload[key] = values[key];
      if (dirty.access_expires_at) payload.access_expires_at = expires;
      if (partyId !== (user.party_id ?? null)) payload.party_id = partyId;
      const rolesChanged = [...values.roles].sort().join() !== [...(user.roles ?? [])].sort().join();
      if (rolesChanged) payload.roles = values.roles;
      if (Object.keys(payload).length === 0) {
        onDone();
        return;
      }
    }
    try {
      await mutation.mutateAsync(payload);
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['name', 'email', 'roles', 'status', 'locale', 'access_expires_at', 'party_id'] });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} noValidate className="flex flex-1 flex-col gap-4 px-4">
        <FormRootError />
        <InputField name="name" label="Name" autoComplete="off" />
        <InputField name="email" label="E-Mail" type="email" autoComplete="off" />
        <RoleCheckboxes roles={roles} hiddenCount={hiddenCount} />
        {!create && <SelectField name="status" label="Status" options={STATUS_OPTIONS} />}
        <SelectField name="locale" label="Sprache" options={LOCALE_OPTIONS} />
        <InputField
          name="access_expires_at"
          label="Zugang gültig bis"
          type="date"
          description="Befristeter Zusatz-/Demo-Zugang: gilt einschließlich dieses Tages. Leer lassen (oder Datum löschen) für unbefristeten Zugang."
        />
        <PartyLinkField user={user} party={party} />
        {create && <CheckboxField name="send_invitation" label="Einladung sofort per E-Mail senden" />}
        <SheetFooter className="px-0">
          <Button type="submit" loading={form.formState.isSubmitting}>
            {create ? 'Anlegen' : 'Speichern'}
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
        </SheetFooter>
      </form>
    </Form>
  );
}

/** Verknüpfte Person: bestimmt, was ein Portalbenutzer sieht (Eigentum, Rollen an Liegenschaften, Mandanten). */
function PartyLinkField({ user, party }) {
  const form = useFormContext();
  const value = form.watch('party_id') || '';
  const known = party?.display_name ?? user?.party?.display_name ?? null;
  const error = form.formState.errors.party_id?.message;
  return (
    <div className="flex flex-col gap-1">
      <PartyPicker
        label="Verknüpfte Person / Organisation"
        value={value}
        valueLabel={known}
        onChange={(id) => form.setValue('party_id', id, { shouldDirty: true })}
        emptyLabel="Keine Person verknüpft"
      />
      <p className="text-xs text-muted-foreground">
        Nötig für Portalzugänge: Sichtbarkeit im Kundenportal ergibt sich aus Eigentum, Rollen an Liegenschaften und Mandanten dieser Person.
      </p>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
